import Button from "components/atoms/Button";
import InputCheckbox from "components/atoms/InputCheckbox";
import InputDate from "components/atoms/InputDate";
import InputField from "components/atoms/InputField";
import InputRadio from "components/atoms/InputRadio";
import InputSelect from "components/atoms/InputSelect";
import { BreadcrumbNav } from "components/molecules";
import { useFormik } from "formik";
import moment from "moment";
import React from "react";
import { useState } from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  errorToastNotif,
  getDataFromLocalStorage,
  nullConverter,
  successToastNotif,
} from "utils/common";
import {
  DiseaseDegreeOptions,
  G6PDResultOptions,
  GametositOptions,
  LabCheckingOptionsRujukan,
  PageMode,
  RecurrenceOptions,
  SymthomaticOptions,
  TreatmentOptions,
  YesAndNoOptions,
} from "utils/constant";
import Rujukan from "services/Rujukan";
import BkkRujukan from "services/BKKRegmalReference";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ClipLoader } from "react-spinners";
import { FiAlertCircle, FiRefreshCw } from "react-icons/fi";
import standardTreatmentService, {
  errorMessages,
} from "services/StandardTreatment";
import { confirmAlert } from "react-confirm-alert";

const RujukanService = new Rujukan();
const BkkRujukanService = new BkkRujukan();

// Spesies parasit -> kode Condition backend (commons/constant.js ConditionField.Parasite).
const PARASITE_CODE = {
  "P.Falciparum": "FALCIPARUM",
  "P.Malariae": "MALARIAE",
  "Probable Knowlesi": "PROBABLE_KNOWLESI",
  "P.Vivax": "VIVAX",
  "P.Ovale": "OVALE",
  "P.Mix": "MIX",
};

// "Tidak dilakukan" -> NOT_DONE mengikuti mapG6pd di seed backend (policy_csv_parser.js).
const G6PD_RESULT_CODE = {
  Normal: "NORMAL",
  Intermediate: "INTERMEDIATE",
  Defisiensi: "DEFICIENT",
  "Tidak dilakukan": "NOT_DONE",
};

const MEDICATION_KEYS = [
  "dhp_tablet",
  "dhp_dispersible",
  "primaquin_tablet",
  "artemeter_lumefantrine",
  "artesunate_pyronaridine",
  "kina_tablet",
  "klindamisin",
  "doksisiklin",
  "tetrasiklin",
  "kina_injeksi",
  "artesunat_injeksi",
];

const STATUS_PARAM_KEYS = [
  "disease_degree",
  "weight",
  "age",
  "age_type",
  "gender",
  "pregnancy",
  "recurrence",
  "parasyte",
  "g6pd",
  "g6pd_result",
  "is_lab_rechecking",
  "lab_rechecking_type",
  ...MEDICATION_KEYS,
];

// Pemetaan ke bentuk /standard-treatment/evaluate, sama dengan Regmal 1.
// lab_checking_type: "Ya" -> jenis pemeriksaan form rujukan; "Tidak"/kosong ->
// jenis pemeriksaan Regmal 1 asal (sourceLabCheckingType).
export const statusParams = (values, sourceLabCheckingType) => ({
  disease_degree: nullConverter(values.disease_degree),
  recurrence: nullConverter(values.recurrence),
  g6pd_result:
    values.g6pd === "Ya" ? G6PD_RESULT_CODE[values.g6pd_result] || null : "NOT_DONE",
  parasite: (values.parasyte || []).map((name) => PARASITE_CODE[name]).filter(Boolean),
  age_months: Number(values.age)
    ? values.age_type === "Tahun"
      ? Number(values.age) * 12
      : Number(values.age)
    : null,
  weight_kg: Number(values.weight) ? Number(values.weight) : null,
  pregnancy: values.pregnancy === "Hamil",
  // Form rujukan tidak punya field menyusui. Backend menolak null (wajib hadir),
  // dan mesin evaluasi membaca field yang tidak dikirim sebagai false.
  breastfeeding: false,
  lab_checking_type:
    values.is_lab_rechecking === "Ya"
      ? nullConverter(values.lab_rechecking_type)
      : nullConverter(sourceLabCheckingType),
  ...Object.fromEntries(
    MEDICATION_KEYS.map((key) => [key, Number(values[key]) > 0 ? Number(values[key]) : null]),
  ),
});

const FormComponent = ({ pageMode, handlePageModeChange, isBkk }) => {
  const [itemToEdit, setItemToEdit] = useState(null);

  const { currentUser } = useSelector((state) => state.authReducer);

  const formik = useFormik({
    initialValues: {
      regmal_id: "",
      ktp: "",
      name: "",
      age: "",
      gender: "",
      source_fasyankes_id: "",
      ref_fasyankes_id: "",
      ref_date: "",
      ref_patient_arrival_date: "",
      is_lab_rechecking: "",
      lab_rechecking_type: "",
      no_registrasi_pasien: "",
      slide_code: "",
      total_rdt: "",
      lab_rechecking_date: "",
      parasyte: [],
      disease_degree: "",
      body_temperature: "",
      sympthomatic: "",
      other_sympthomatic: "",
      sympthomatic_date: "",
      gametosit: "",
      parasyte_density: "",
      weight: "",
      medication_date: "",
      medication: "",
      standard_medication_status: "",
      dhp_tablet: "",
      primaquin_tablet: "",
      kina_tablet: "",
      artesunat_injeksi: "",
      kina_injeksi: "",
      doksisiklin: "",
      tetrasiklin: "",
      klindamisin: "",
      dhp_dispersible: "",
      artemeter_lumefantrine: "",
      artesunate_pyronaridine: "",
      treatment: "",
      death_with_malaria: "",
      death_caused_by_malaria: "",
      status: "",
    },
    validate: (values) => {
      const errors = {};

      if (isArrive) {
        if (!values.ref_patient_arrival_date) {
          errors.ref_patient_arrival_date =
            "Tanggal pasien rujukan datang wajib diisi";
        }

        if (!values.is_lab_rechecking) {
          errors.is_lab_rechecking =
            "Apakah dilakukan pemeriksaan ulang wajib diisi";
        }

        if (values.is_lab_rechecking === "Ya") {
          if (!values.lab_rechecking_type) {
            errors.lab_rechecking_type = "Jenis pemeriksaan wajib diisi";
          }

          if (!values.lab_rechecking_date) {
            errors.lab_rechecking_date = "Tanggal pemeriksaan wajib disii";
          }
          if (!values.parasyte || values.parasyte.length === 0) {
            errors.parasyte = "Parasyte wajib diisi";
          }
        }

        if (!values.treatment) {
          errors.treatment = "Perawatan wajib diisi";
        }

        if (!values.death_with_malaria) {
          errors.death_with_malaria = "Kematian dengan malaria wajib diisi";
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      let medication = {
        dhp_tablet: values.dhp_tablet,
        primaquin_tablet: values.primaquin_tablet,
        kina_injeksi: values.kina_injeksi,
        kina_tablet: values.kina_tablet,
        doksisiklin: values.doksisiklin,
        tetrasiklin: values.tetrasiklin,
        klindamisin: values.klindamisin,
        artesunat_injeksi: values.artesunat_injeksi,
        dhp_dispersible: values.dhp_dispersible,
        artemeter_lumefantrine: values.artemeter_lumefantrine,
        artesunate_pyronaridine: values.artesunate_pyronaridine,
      };

      let payload = {
        regmal_id: values.regmal_id,
        ktp: values.ktp,
        name: values.name,
        age: values.age,
        gender: values.gender,
        source_fasyankes_id: values.source_fasyankes_id,
        ref_fasyankes_id: values.ref_fasyankes_id,
        ref_date: values.ref_date,
        ref_patient_arrival_date: values.ref_patient_arrival_date,
        is_lab_rechecking: values.is_lab_rechecking,
        lab_rechecking_type: values.lab_rechecking_type,
        nomor_urut_slide: values.nomor_urut_slide,
        slide_code: values.slide_code,
        total_rdt: values.total_rdt,
        lab_rechecking_date: values.lab_rechecking_date,
        parasyte: values.parasyte,
        disease_degree: values.disease_degree,
        body_temperature: values.body_temperature,
        sympthomatic: values.sympthomatic,
        sympthomatic_date: values.sympthomatic_date,
        gametosit: values.gametosit,
        parasyte_density: values.parasyte_density,
        weight: values.weight,
        medication_date: values.medication_date,
        medication: medication,
        standard_medication_status: values.standard_medication_status,
        treatment: values.treatment,
        death_with_malaria: values.death_with_malaria,
        death_caused_by_malaria: values.death_caused_by_malaria,
        status: "Sudah",
        recurrence: values.recurrence,
        g6pd: values.g6pd,
        g6pd_result: values.g6pd_result,
      };

      try {
        if (isArrive) {
          if (isBkk) {
            await BkkRujukanService.update(payload, itemToEdit?.id)
              .then((res) => {
                handlePageModeChange(PageMode.view);
                successToastNotif("Data Rujukan Berhasil diupdate");
              })
              .catch((err) => {
                console.log(err);
                errorToastNotif(err);
              });
          } else {
            await RujukanService.update(payload, itemToEdit?.id)
              .then((res) => {
                handlePageModeChange(PageMode.view);
                successToastNotif("Data Rujukan Berhasil diupdate");
              })
              .catch((err) => {
                console.log(err);
                errorToastNotif(err);
              });
          }
        } else {
          if (isBkk) {
            await BkkRujukanService.update(
              { status: "Pasien Tidak Datang" },
              itemToEdit?.id,
            )
              .then((res) => {
                handlePageModeChange(PageMode.view);
                successToastNotif("Data Rujukan Berhasil diupdate");
              })
              .catch((err) => {
                console.log(err);
                errorToastNotif(err);
              });
          } else {
            await RujukanService.update(
              { status: "Pasien Tidak Datang" },
              itemToEdit?.id,
            )
              .then((res) => {
                handlePageModeChange(PageMode.view);
                successToastNotif("Data Rujukan Berhasil diupdate");
              })
              .catch((err) => {
                console.log(err);
                errorToastNotif(err);
              });
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
  });

  const [isArrive, setIsArrive] = useState(
    formik.values.is_patient_arrive == undefined ? true : false,
  );

  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    const value = e.target.value;

    if (value === "P.Mix") {
      const newValues = isChecked ? ["P.Mix"] : [];
      formik.setFieldValue("parasyte", newValues);
    } else {
      const newValues = isChecked
        ? [...formik.values.parasyte, value]
        : formik.values.parasyte.filter((item) => item !== value);
      formik.setFieldValue("parasyte", newValues);
    }
  };

  const generateSlideCode = () => {
    let slideCode = "";

    const districtId = currentUser?.district_id?.split("|")[0];
    const fasyankesId = currentUser?.fasyankes_id?.split("|")[0];
    const nomorUrutSlide = formik.values.nomor_urut_slide || 0;
    const monthLabChecking =
      formik.values.lab_rechecking_date?.split("-")[1] || 0;
    const yearLabChecking =
      formik.values.lab_rechecking_date?.split("-")[0] || 0;

    slideCode = `${districtId}/${fasyankesId}/${nomorUrutSlide}/${monthLabChecking}/${yearLabChecking}`;

    formik.setFieldValue("slide_code", slideCode);
  };

  const getRecurrenceOptions = (parasyteValue) => {
    const commonOptions = [{ label: "Baru", value: "Baru" }];

    if (
      parasyteValue?.includes("P.Ovale") ||
      parasyteValue?.includes("P.Vivax") ||
      parasyteValue?.includes("P.Mix")
    ) {
      return [...commonOptions, { label: "Relaps", value: "Relaps" }];
    }

    return commonOptions;
  };

  const [checkStatusLoading, setCheckStatusLoading] = useState(false);
  // Ditandai saat nilai parameter status berubah SETELAH pengecekan terakhir.
  const [statusChanged, setStatusChanged] = useState(false);
  const [lastCheckedParams, setLastCheckedParams] = useState(null);

  // "Tidak"/kosong -> jenis pemeriksaan Regmal 1 asal; "Ya" -> jenis pemeriksaan rujukan.
  const sourceLabCheckingType = isBkk
    ? itemToEdit?.bkk_regmal?.lab_checking_type
    : itemToEdit?.regmal?.lab_checking_type;
  const currentStatusParams = () => statusParams(formik.values, sourceLabCheckingType);

  useEffect(() => {
    if (!lastCheckedParams) return;
    setStatusChanged(
      JSON.stringify(currentStatusParams()) !==
        JSON.stringify(lastCheckedParams),
    );
  }, [...STATUS_PARAM_KEYS.map((key) => formik.values[key]), sourceLabCheckingType]);

  const checkStatusMedication = async () => {
    const snapshot = currentStatusParams();
    setCheckStatusLoading(true);
    try {
      const result = await standardTreatmentService.evaluate({
        patientFacts: snapshot,
        medicationGiven: Object.fromEntries(
          MEDICATION_KEYS.map((key) => [key, snapshot[key]]),
        ),
      });
      setLastCheckedParams(snapshot);
      setStatusChanged(false);
      formik.setFieldValue("standard_medication_status", result.status);
    } catch (err) {
      toast.error(errorMessages(err).join(", "), {
        position: "top-right",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setCheckStatusLoading(false);
    }
  };

  useEffect(() => {
    if (pageMode === PageMode.edit) {
      const selectedItem = JSON.parse(getDataFromLocalStorage("SELECTED_ITEM"));
      setItemToEdit(selectedItem);
      if (`selectedItem`?.status == "Pasien Tidak Datang") {
        setIsArrive(false);
      }

      const editValues = {
        regmal_id: selectedItem?.regmal_id,
        ktp: selectedItem?.ktp,
        name: selectedItem?.name,
        age: selectedItem?.age,
        age_type: selectedItem?.age_type,
        pregnancy: isBkk
          ? selectedItem?.bkk_regmal.pregnancy
          : selectedItem?.regmal.pregnancy,
        recurrence: selectedItem?.recurrence,
        g6pd: selectedItem?.g6pd,
        g6pd_result: selectedItem?.g6pd_result,
        gender: selectedItem?.gender,
        source_fasyankes_id: selectedItem?.source_fasyankes_id,
        ref_fasyankes_id: selectedItem?.ref_fasyankes_id,
        ref_date: selectedItem?.ref_date,
        ref_patient_arrival_date: selectedItem?.ref_patient_arrival_date,
        is_lab_rechecking: selectedItem?.is_lab_rechecking,
        lab_rechecking_date: selectedItem?.lab_rechecking_date,
        nomor_urut_slide: selectedItem?.nomor_urut_slide,
        slide_code: selectedItem?.slide_code,
        total_rdt: selectedItem?.total_rdt,
        lab_rechecking_type: selectedItem?.lab_rechecking_type,
        parasyte: selectedItem?.parasyte,
        disease_degree: selectedItem?.disease_degree,
        body_temperature: selectedItem?.body_temperature,
        sympthomatic: selectedItem?.sympthomatic,
        sympthomatic_date: selectedItem?.sympthomatic_date,
        gametosit: selectedItem?.gametosit,
        parasyte_density: selectedItem?.parasyte_density,
        weight: selectedItem?.weight,
        medication_date: selectedItem?.medication_date,
        dhp_tablet: selectedItem?.medication?.dhp_tablet,
        primaquin_tablet: selectedItem?.medication?.primaquin_tablet,
        kina_tablet: selectedItem?.medication?.kina_tablet,
        kina_injeksi: selectedItem?.medication?.kina_injeksi,
        artesunat_injeksi: selectedItem?.medication?.artesunat_injeksi,
        klindamisin: selectedItem?.medication?.klindamisin,
        doksisiklin: selectedItem?.medication?.doksisiklin,
        tetrasiklin: selectedItem?.medication?.tetrasiklin,
        dhp_dispersible: selectedItem?.medication?.dhp_dispersible,
        artemeter_lumefantrine:
          selectedItem?.medication?.artemeter_lumefantrine,
        artesunate_pyronaridine:
          selectedItem?.medication?.artesunate_pyronaridine,
        standard_medication_status: selectedItem?.standard_medication_status,
        treatment: selectedItem?.treatment,
        death_with_malaria: selectedItem?.death_with_malaria,
        is_patient_arrive:
          selectedItem?.status == "Pasien Tidak Datang" ? "Tidak" : null,
      };
      formik.setValues(editValues);
      // Baseline memakai objek yang sama dengan yang di-set ke formik supaya bentuknya
      // tidak bisa drift dari currentStatusParams(). Tipe lab diambil eksplisit dari
      // selectedItem karena state itemToEdit belum ter-commit di efek yang sama.
      setLastCheckedParams(
        statusParams(
          editValues,
          isBkk
            ? selectedItem?.bkk_regmal?.lab_checking_type
            : selectedItem?.regmal?.lab_checking_type,
        ),
      );
    }
  }, []);

  useEffect(() => {
    if (formik.values.lab_rechecking_type === "Mikroskop") {
      generateSlideCode();
    }
  }, [
    formik.values.no_registrasi_pasien,
    formik.values.nomor_urut_slide,
    formik.values.lab_rechecking_date,
    formik.values.lab_rechecking_type,
  ]);

  // Simpan data hanya boleh setelah "Cek Status" bila parameter berubah.
  const handleSaveData = () => {
    if (isArrive && statusChanged) {
      confirmAlert({
        title: "Cek Status Pengobatan Terlebih Dahulu",
        message:
          "Ada parameter pengobatan yang berubah sejak pengecekan terakhir. Klik “Cek Status” untuk memperbarui status pengobatan sebelum menyimpan data.",
        buttons: [
          { label: "Tutup", onClick: () => {} },
          { label: "Cek Status Sekarang", onClick: checkStatusMedication },
        ],
      });
      return;
    }
    formik.handleSubmit();
  };

  return (
    <>
      <BreadcrumbNav
        items={[
          {
            label: "Regmal 1 - Rujukan",
            onClick: () => handlePageModeChange(PageMode.view),
          },
          { label: "Edit Data Rujukan" },
        ]}
      />
      <h1 className="page-title">Edit Data Rujukan</h1>

      <div className="table-responsive">
        <table className="table table-hover">
          <tbody>
            <tr>
              <td width={"300"}>Nama</td>
              <td width={"10"}>:</td>
              <td>
                <span style={{ fontWeight: "600" }}>{itemToEdit?.name}</span>
              </td>
            </tr>
            <tr>
              <td width={"300"}>NIK</td>
              <td width={"10"}>:</td>
              <td>
                <span style={{ fontWeight: "600" }}>{itemToEdit?.ktp}</span>
              </td>
            </tr>
            <tr>
              <td width={"300"}>Jenis Kelamin</td>
              <td width={"10"}>:</td>
              <td>
                <span style={{ fontWeight: "600" }}>{itemToEdit?.gender}</span>
              </td>
            </tr>
            {/* only show if jenis kelamin perempuan */}
            {itemToEdit?.gender === "Perempuan" && (
              <tr>
                <td width={"300"}>Status Kehamilan</td>
                <td width={"10"}>:</td>
                <td>
                  <span style={{ fontWeight: "600" }}>
                    {isBkk
                      ? itemToEdit?.bkk_regmal?.pregnancy
                      : itemToEdit?.regmal?.pregnancy}
                  </span>
                </td>
              </tr>
            )}
            <tr>
              <td width={"300"}>Usia</td>
              <td width={"10"}>:</td>
              <td>
                <span style={{ fontWeight: "600" }}>
                  {itemToEdit?.age} {itemToEdit?.age_type}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="form-grouping">
        <p
          className="warning"
          style={{ marginBottom: "24px", marginTop: "24px" }}
        >
          NOTE<span>*(WAJIB DIISI)</span>
        </p>

        <InputRadio
          label="Apakah Pasien datang ke faskes rujukan?"
          name="is_patient_arrive"
          options={YesAndNoOptions}
          onChange={(e) => {
            const selectedValue = e.target.value;
            formik.handleChange("is_patient_arrive")(selectedValue);
            setIsArrive(selectedValue == "Ya" ? true : false);
          }}
          onBlur={formik.handleBlur}
          selectedOption={formik.values.is_patient_arrive}
          error={
            formik.errors.is_patient_arrive && formik.touched.is_patient_arrive
          }
          errorMessage={formik.errors.is_patient_arrive}
          isRequired={true}
        />

        {!isArrive && (
          <div className="warning-message">
            <div className="message">Pasien Tidak Datang</div>
          </div>
        )}

        {isArrive && (
          <>
            <InputDate
              label={"Tanggal Pasien Rujukan Datang"}
              name={"ref_patient_arrival_date"}
              placeholder={"Pilih Tanggal"}
              dateFormat={"YYYY-MM-DD"}
              onChange={(e) => {
                const selectedDate = moment(e).format("YYYY-MM-DD");
                formik.handleChange("ref_patient_arrival_date")(selectedDate);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.ref_patient_arrival_date}
              error={
                formik.errors.ref_patient_arrival_date &&
                formik.touched.ref_patient_arrival_date
              }
              errorMessage={formik.errors.ref_patient_arrival_date}
              isRequired={true}
            />

            <InputRadio
              label="Apakah pasien yang dirujuk dilakukan pemeriksaan lab ulang?"
              name="is_lab_rechecking"
              options={YesAndNoOptions}
              onChange={(e) => {
                const selectedValue = e.target.value;
                formik.handleChange("is_lab_rechecking")(selectedValue);
              }}
              onBlur={formik.handleBlur}
              selectedOption={formik.values.is_lab_rechecking}
              error={
                formik.errors.is_lab_rechecking &&
                formik.touched.is_lab_rechecking
              }
              errorMessage={formik.errors.is_lab_rechecking}
              isRequired={true}
            />

            {formik.values.is_lab_rechecking === "Ya" && (
              <>
                <InputSelect
                  label={"Jenis Pemeriksaan"}
                  name={"lab_rechecking_type"}
                  placeholder={"Pilih Pemeriksaan"}
                  data={LabCheckingOptionsRujukan}
                  onChange={(e) => {
                    const selectedValue = e ? e.value : "";
                    formik.handleChange("lab_rechecking_type")(selectedValue);

                    if (selectedValue === "Mikroskop") {
                      formik.setFieldValue(
                        "parasyte",
                        formik.values.parasyte?.filter(
                          (item) => item !== "P>Mix",
                        ),
                      );
                    }
                  }}
                  onBlur={formik.handleBlur}
                  value={LabCheckingOptionsRujukan.find(
                    (item) => item.value === formik.values.lab_rechecking_type,
                  )}
                  error={
                    formik.errors.lab_rechecking_type &&
                    formik.touched.lab_rechecking_type
                  }
                  errorMessage={formik.errors.lab_rechecking_type}
                  isRequired={true}
                />

                {formik.values.lab_rechecking_type === "Mikroskop" && (
                  <InputField
                    label={"Nomor Registrasi Pasien"}
                    name={"no_registrasi_pasien"}
                    maxLengthVal={20}
                    placeholder={"Masukkan Nomor Registrasi Pasien"}
                    {...formik.getFieldProps("no_registrasi_pasien")}
                  />
                )}

                <InputField
                  label={"Jumlah RDT yang digunakan"}
                  name={"total_rdt"}
                  placeholder={"Masukkan Jumlah RDT"}
                  {...formik.getFieldProps("total_rdt")}
                  error={formik.errors.total_rdt && formik.touched.total_rdt}
                  errorMessage={formik.errors.total_rdt}
                  isRequired={true}
                />

                <InputDate
                  label={"Tanggal Pemeriksaan Lab"}
                  name={"lab_rechecking_date"}
                  placeholder={"Pilih Tanggal"}
                  dateFormat={"YYYY-MM-DD"}
                  onChange={(e) => {
                    const selectedDate = moment(e).format("YYYY-MM-DD");
                    formik.handleChange("lab_rechecking_date")(selectedDate);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.lab_rechecking_date}
                  error={
                    formik.errors.lab_rechecking_date &&
                    formik.touched.lab_rechecking_date
                  }
                  errorMessage={formik.errors.lab_rechecking_date}
                  isRequired={true}
                />

                {formik.values.lab_rechecking_type === "Mikroskop" && (
                  <>
                    <InputField
                      label={"Nomor urut slide"}
                      name={"nomor_urut_slide"}
                      placeholder={"Masukkan nomor urut slide"}
                      value={formik.values.nomor_urut_slide}
                      onChange={(e) => {
                        const value = e.target.value;
                        formik.setFieldValue("nomor_urut_slide", value);
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        formik.errors.nomor_urut_slide &&
                        formik.touched.nomor_urut_slide
                      }
                      errorMessage={formik.errors.nomor_urut_slide}
                      isRequired={true}
                    />
                    <InputField
                      label={"Kode Slide"}
                      name={"slide_code"}
                      placeholder={"Kode Slide Otomatis"}
                      value={formik.values.slide_code}
                      disabled={true}
                    />
                  </>
                )}

                <InputCheckbox
                  label="Jenis Parasit"
                  name="parasyte"
                  options={[
                    { label: "P.falciparum", value: "P.Falciparum" },
                    { label: "P.malariae", value: "P.Malariae" },
                    { label: "Probable knowlesi", value: "Probable Knowlesi" },
                    { label: "P.vivax", value: "P.Vivax" },
                    { label: "P.ovale", value: "P.Ovale" },
                    ...(formik.values.lab_rechecking_type === "RDT"
                      ? [{ label: "P.mix", value: "P.Mix" }]
                      : []),
                  ]}
                  onChange={(e) => handleCheckboxChange(e)}
                  onBlur={formik.handleBlur}
                  selectedOption={formik.values.parasyte}
                  isItalic={true}
                  error={formik.errors.parasyte && formik.touched.parasyte}
                  errorMessage={formik.errors.parasyte}
                  isRequired={true}
                />
              </>
            )}

            <InputRadio
              label="Derajat Penyakit Malaria"
              name="disease_degree"
              options={DiseaseDegreeOptions}
              onChange={(e) => {
                const selectedValue = e.target.value;
                formik.handleChange("disease_degree")(selectedValue);
              }}
              onBlur={formik.handleBlur}
              selectedOption={formik.values.disease_degree}
            />

            <div className="row">
              <div className="col-sm-6">
                <InputField
                  label={"Suhu Tubuh"}
                  name={"body_temperature"}
                  placeholder={"Masukkan Suhu Tubuh"}
                  type={"number"}
                  maxLengthVal={3}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    formik.handleChange("body_temperature")(selectedValue);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.body_temperature}
                />
              </div>
              <div className="col-sm-6">
                <InputSelect
                  label={"Gejala"}
                  name={"sympthomatic"}
                  placeholder={"Pilih Gejala"}
                  data={SymthomaticOptions}
                  onChange={(e) => {
                    const selectedValue = e ? e.value : "";
                    formik.handleChange("sympthomatic")(selectedValue);
                    if (selectedValue !== "Lainnya") {
                      formik.setFieldValue("other_sympthomatic", "");
                    }
                  }}
                  onBlur={formik.handleBlur}
                  value={SymthomaticOptions?.find(
                    (item) => item.value === formik.values.sympthomatic,
                  )}
                />
                {formik.values.sympthomatic === "Lainnya" && (
                  <InputField
                    name={"other_sympthomatic"}
                    placeholder={"Masukkan Gejala Lainnya"}
                    type={"text"}
                    {...formik.getFieldProps("other_sympthomatic")}
                    error={
                      formik.errors.other_sympthomatic &&
                      formik.touched.other_sympthomatic
                    }
                    errorMessage={formik.errors.other_sympthomatic}
                  />
                )}
              </div>
            </div>

            {formik.values.sympthomatic !== "Tidak bergejala" &&
              formik.values.sympthomatic !== null &&
              formik.values.sympthomatic !== "" && (
                <InputDate
                  label={"Tanggal Gejala"}
                  name={"sympthomatic_date"}
                  placeholder={"Pilih Tanggal"}
                  dateFormat={"YYYY-MM-DD"}
                  onChange={(e) => {
                    const selectedDate = moment(e).format("YYYY-MM-DD");
                    formik.handleChange("sympthomatic_date")(selectedDate);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.sympthomatic_date}
                  error={
                    formik.errors.sympthomatic_date &&
                    formik.touched.sympthomatic_date
                  }
                  errorMessage={formik.errors.sympthomatic_date}
                  isRequired={
                    formik.values.sympthomatic === "Tidak bergejala"
                      ? false
                      : true
                  }
                />
              )}

            <InputRadio
              label="Gametosit"
              name="gametosit"
              options={GametositOptions}
              onChange={(e) => {
                const selectedValue = e.target.value;
                formik.handleChange("gametosit")(selectedValue);
              }}
              onBlur={formik.handleBlur}
              selectedOption={formik.values.gametosit}
            />

            <div className="row">
              <div className="col-sm-6">
                <InputField
                  label={"Kepadatan Parasit(Per mikro liter)"}
                  name={"parasyte_density"}
                  placeholder={"Masukkan Kepadatan Parasit"}
                  type={"number"}
                  maxLengthVal={11}
                  {...formik.getFieldProps("parasyte_density")}
                />
              </div>
              <div className="col-sm-6">
                <InputField
                  label={"Berat Badan (Kg)"}
                  name={"weight"}
                  placeholder={"Masukkan Berat Badan"}
                  type={"number"}
                  maxLengthVal={11}
                  {...formik.getFieldProps("weight")}
                />
              </div>
            </div>

            <InputSelect
              label={"Kekambuhan"}
              name={"recurrence"}
              placeholder={"Pilih Kekambuhan"}
              data={getRecurrenceOptions(formik.values.parasyte)}
              onChange={(e) => {
                const selectedValue = e ? e.value : "";
                formik.handleChange("recurrence")(selectedValue);
              }}
              onBlur={formik.handleBlur}
              value={RecurrenceOptions?.find(
                (item) => item.value === formik.values.recurrence,
              )}
              error={formik.errors.recurrence && formik.touched.recurrence}
              errorMessage={formik.errors.recurrence}
              isRequired={true}
            />

            {formik.values.recurrence === "Relaps" && (
              <InputSelect
                label={"Apakah Pernah Dilakukan Pemeriksaan G6PD ? (Ya/Tidak)"}
                name={"g6pd"}
                placeholder={"Pilih"}
                data={YesAndNoOptions}
                onChange={(e) => {
                  const selectedValue = e ? e.value : "";
                  if (selectedValue) {
                    formik.handleChange("g6pd")(selectedValue);

                    if (selectedValue === "Tidak") {
                      formik.setFieldValue("g6pd_result", "");
                    }
                  }
                }}
                onBlur={formik.handleBlur}
                value={YesAndNoOptions?.find(
                  (item) => item.value === formik.values.g6pd,
                )}
                error={formik.errors.g6pd && formik.touched.g6pd}
                errorMessage={formik.errors.g6pd}
                isRequired={true}
              />
            )}
            {formik.values.recurrence === "Relaps" &&
              formik.values.g6pd === "Ya" && (
                <InputSelect
                  label={"Pilih Hasil Pemeriksaan G6PD"}
                  name={"g6pd_result"}
                  placeholder={"Pilih"}
                  data={G6PDResultOptions}
                  onChange={(e) => {
                    const selectedValue = e ? e.value : "";
                    if (selectedValue) {
                      formik.handleChange("g6pd_result")(selectedValue);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  value={G6PDResultOptions?.find(
                    (item) => item.value === formik.values.g6pd_result,
                  )}
                  error={
                    formik.errors.g6pd_result && formik.touched.g6pd_result
                  }
                  errorMessage={formik.errors.g6pd_result}
                  isRequired={true}
                />
              )}

            <hr />
            <h3 className="subtitle-section">Pengobatan</h3>
            <InputDate
              label={"Tanggal Pengobatan"}
              name={"medication_date"}
              placeholder={"Pilih Tanggal"}
              dateFormat={"YYYY-MM-DD"}
              onChange={(e) => {
                const selectedDate = moment(e).format("YYYY-MM-DD");
                formik.handleChange("medication_date")(selectedDate);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.medication_date}
            />
            <hr />
            <div className="row">
              <div className="col-sm-6">
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#313131",
                  }}
                >
                  DHP Tablet (Tablet)
                </p>
              </div>
              <div className="col-sm-6">
                <InputField
                  name={"dhp_tablet"}
                  placeholder={"0"}
                  type={"decimal"}
                  {...formik.getFieldProps("dhp_tablet")}
                />
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-6">
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#313131",
                  }}
                >
                  Primaquin Tablet (Tablet)
                </p>
              </div>
              <div className="col-sm-6">
                <InputField
                  name={"primaquin_tablet"}
                  placeholder={"0"}
                  type={"decimal"}
                  {...formik.getFieldProps("primaquin_tablet")}
                />
                <div className="warning-message" style={{ marginTop: "12px" }}>
                  <div className="message">
                    Primaquin tidak boleh diberikan kepada ibu hamil, menyusui,
                    dan anak di bawah usia 6 bulan
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-6">
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#313131",
                  }}
                >
                  Artesunat Injeksi (Vial)
                </p>
              </div>
              <div className="col-sm-6">
                <InputField
                  name={"artesunat_injeksi"}
                  placeholder={"0"}
                  type={"decimal"}
                  {...formik.getFieldProps("artesunat_injeksi")}
                />
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-6">
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#313131",
                  }}
                >
                  Kina Tablet (Tablet)
                </p>
              </div>
              <div className="col-sm-6">
                <InputField
                  name={"kina_tablet"}
                  placeholder={"0"}
                  type={"decimal"}
                  {...formik.getFieldProps("kina_tablet")}
                />
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-6">
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#313131",
                  }}
                >
                  Kina Injeksi (Ampul)
                </p>
              </div>
              <div className="col-sm-6">
                <InputField
                  name={"kina_injeksi"}
                  placeholder={"0"}
                  type={"decimal"}
                  {...formik.getFieldProps("kina_injeksi")}
                />
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-6">
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#313131",
                  }}
                >
                  Dosisiklin (Tablet)
                </p>
              </div>
              <div className="col-sm-6">
                <InputField
                  name={"doksisiklin"}
                  placeholder={"0"}
                  type={"decimal"}
                  {...formik.getFieldProps("doksisiklin")}
                />
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-6">
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#313131",
                  }}
                >
                  Tetrasiklin (Tablet)
                </p>
              </div>
              <div className="col-sm-6">
                <InputField
                  name={"tetrasiklin"}
                  placeholder={"0"}
                  type={"decimal"}
                  {...formik.getFieldProps("tetrasiklin")}
                />
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-6">
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#313131",
                  }}
                >
                  Klindamisin (Tablet)
                </p>
              </div>
              <div className="col-sm-6">
                <InputField
                  name={"Klindamisin"}
                  placeholder={"0"}
                  type={"decimal"}
                  {...formik.getFieldProps("klindamisin")}
                />
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-6">
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#313131",
                  }}
                >
                  DHP Dispersible
                </p>
              </div>
              <div className="col-sm-6">
                <InputField
                  name={"dhp_dispersible"}
                  placeholder={"0"}
                  type={"decimal"}
                  {...formik.getFieldProps("dhp_dispersible")}
                />
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-6">
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#313131",
                  }}
                >
                  AL (Artemeter Lumefantrine)
                </p>
              </div>
              <div className="col-sm-6">
                <InputField
                  name={"artemeter_lumefantrine"}
                  placeholder={"0"}
                  type={"decimal"}
                  {...formik.getFieldProps("artemeter_lumefantrine")}
                />
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-6">
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#313131",
                  }}
                >
                  ASPY (Artesunate Pyronaridine)
                </p>
              </div>
              <div className="col-sm-6">
                <InputField
                  name={"artesunate_pyronaridine"}
                  placeholder={"0"}
                  type={"decimal"}
                  {...formik.getFieldProps("artesunate_pyronaridine")}
                />
              </div>
            </div>
            <hr />
            <InputField
              label={"Standar Pengobatan"}
              name={"standard_medication_status"}
              placeholder={"Masukkan Standar Pengobatan"}
              disabled={true}
              value={formik.values.standard_medication_status}
              {...formik.getFieldProps("standard_medication_status")}
            />
            <div
              style={{
                marginTop: "8px",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
                fontSize: "12px",
              }}
            >
              <Button
                type={"button"}
                variant={"secondary"}
                size={"small"}
                onClick={checkStatusMedication}
                isDisabled={checkStatusLoading}
                style={{
                  gap: "6px",
                  border: "1px solid #18b3ab",
                  height: "auto",
                  padding: "6px 12px",
                  fontSize: "12px",
                  lineHeight: 1.4,
                }}
              >
                {checkStatusLoading ? (
                  <>
                    <ClipLoader size={12} color="#18b3ab" />
                    Memeriksa...
                  </>
                ) : (
                  <>
                    <FiRefreshCw size={14} />
                    Cek Status
                  </>
                )}
              </Button>
              {statusChanged && (
                <span
                  style={{
                    display: "flex",
                    gap: "6px",
                    alignItems: "center",
                    fontSize: "12px",
                    lineHeight: 1.4,
                    color: "#8a5200",
                  }}
                >
                  <FiAlertCircle size={14} style={{ flexShrink: 0 }} />
                  Ada parameter berubah. Klik “Cek Status” untuk memperbarui.
                </span>
              )}
            </div>
            <InputSelect
              label={"Perawatan"}
              name={"treatment"}
              placeholder={"Pilih Perawatan"}
              data={TreatmentOptions}
              onChange={(e) => {
                const selectedValue = e ? e.value : "";
                formik.handleChange("treatment")(selectedValue);
              }}
              onBlur={formik.handleBlur}
              value={TreatmentOptions?.find(
                (item) => item.value === formik.values.treatment,
              )}
              error={formik.errors.treatment && formik.touched.treatment}
              errorMessage={formik.errors.treatment}
              isRequired={true}
            />
            <InputRadio
              label="Kematian dengan Malaria (kasus kematian yang disebabkan penyakit lain tetapi ditemukan parasit malaria/ada penyerta penyakit lain)?"
              name="death_with_malaria"
              options={YesAndNoOptions}
              onChange={(e) => {
                const selectedValue = e.target.value;
                formik.handleChange("death_with_malaria")(selectedValue);
              }}
              onBlur={formik.handleBlur}
              selectedOption={formik.values.death_with_malaria}
              error={
                formik.errors.death_with_malaria &&
                formik.touched.death_with_malaria
              }
              errorMessage={formik.errors.death_with_malaria}
              isRequired={true}
            />

            <InputRadio
              label="Kematian Akibat Malaria (malaria sebagai penyebab utama kematian/penyebab langsung)?"
              name="death_caused_by_malaria"
              options={YesAndNoOptions}
              onChange={(e) => {
                const selectedValue = e.target.value;
                formik.handleChange("death_caused_by_malaria")(selectedValue);
              }}
              onBlur={formik.handleBlur}
              selectedOption={formik.values.death_caused_by_malaria}
            />
          </>
        )}
        <hr />

        <div className="form_action">
          <Button
            onClick={() => handlePageModeChange(PageMode.view)}
            variant={"tertiary"}
            size={"normal"}
          >
            Kembali
          </Button>
          {isArrive ? (
            <Button
              variant={"primary"}
              size={"normal"}
              isDisabled={
                formik.isSubmitting ||
                !formik.values.ref_patient_arrival_date ||
                !formik.values.is_lab_rechecking ||
                (formik.values.sympthomatic !== "Tidak bergejala" &&
                  formik.values.sympthomatic &&
                  !formik.values.sympthomatic_date) ||
                !formik.values.treatment ||
                !formik.values.death_with_malaria
              }
              onClick={handleSaveData}
            >
              Simpan Data
            </Button>
          ) : (
            <Button
              variant={"primary"}
              size={"normal"}
              onClick={handleSaveData}
            >
              Simpan Data
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default FormComponent;
